import { useContext, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CancelOutlined,
  CheckCircle,
  Close,
  East,
  InfoOutlined,
} from '@mui/icons-material';
import { getPartnerLogo } from '../../service/logo.service';
import { TripFiltersContext } from '../../context/trip-filters.context';
import { usePlanPrice, useSelectInsurance } from '../../hooks/plan.hooks';
import {
  coversCovid,
  formatBRL,
  formatLuggageCoverage,
  formatMedicalCoverage,
  getLuggageText,
  luggageCoverageValue,
} from '../../utils/coverage.utils';
import { buildCoverageDetails } from '../../utils/coverage-details.utils';

// Quadrinho do resumo no topo (despesas médicas, bagagem, Covid, cancelamento).
const SummaryTile = ({ label, value, muted }) => (
  <Box
    sx={{
      p: { xs: 1.25, md: 1.75 },
      borderRadius: 2,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'grey.100',
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: '1rem', md: '1.15rem' },
        color: muted ? 'text.disabled' : 'text.primary',
        lineHeight: 1.25,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// Linha de uma cobertura: ícone + nome à esquerda, valor à direita.
const CoverageRow = ({ item }) => {
  const icon =
    item.status === 'no' ? (
      <CancelOutlined sx={{ color: 'text.disabled', fontSize: 20 }} />
    ) : (
      <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
    );

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'grey.100' }}
    >
      <Box sx={{ pt: '2px', flexShrink: 0 }}>{icon}</Box>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: { xs: '0.9rem', md: '0.95rem' },
          color: item.status === 'no' ? 'text.disabled' : 'text.primary',
          lineHeight: 1.4,
        }}
      >
        {item.label}
      </Typography>
      <Box sx={{ textAlign: 'right', flexShrink: 0, maxWidth: '45%' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.9rem', md: '0.95rem' },
            color: item.status === 'no' ? 'text.disabled' : 'text.primary',
            whiteSpace: item.status === 'text' ? 'normal' : 'nowrap',
          }}
        >
          {item.display}
        </Typography>
        {item.detail && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.detail}</Typography>
        )}
      </Box>
    </Stack>
  );
};

// Janela "Ver cobertura completa". No celular ocupa a tela inteira.
const CoverageDialog = ({ insurance, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { destination } = useContext(TripFiltersContext);
  const isEurope = destination === 'Europa';
  const { total, perDay, passengers } = usePlanPrice(insurance);
  const selectInsurance = useSelectInsurance();

  const details = useMemo(
    () => (open ? buildCoverageDetails(insurance, isEurope) : null),
    [open, insurance, isEurope]
  );

  if (!open || !details) return null;

  const luggage = getLuggageText(insurance);
  const cancellation = details.sections
    .flatMap((s) => s.items)
    .find((item) => /cancelamento/i.test(item.label) && !/voo/i.test(item.label));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: 'hidden' } }}
    >
      {/* Cabeçalho fixo */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 2 },
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            height: { xs: 28, md: 36 },
            width: { xs: 90, md: 120 },
            flexShrink: 0,
            '& svg': { height: '100%', width: '100%' },
          }}
        >
          {getPartnerLogo(insurance.insuranceCompany)?.component.dark}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 800, color: 'primary.main', fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.2 }}
          >
            {insurance.insuranceName}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Cobertura completa</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fechar" sx={{ flexShrink: 0 }}>
          <Close />
        </IconButton>
      </Box>

      {/* Conteúdo com rolagem */}
      <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 2, md: 3 }, pb: 3 }}>
        {/* Resumo */}
        <Box
          sx={{
            mt: 2,
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          <SummaryTile
            label="Despesas médicas"
            value={formatMedicalCoverage(insurance.healthCoverage, isEurope)}
          />
          <SummaryTile
            label="Bagagem extraviada"
            value={luggageCoverageValue(luggage) ? formatLuggageCoverage(luggage, isEurope) : 'Não cobre'}
            muted={!luggageCoverageValue(luggage)}
          />
          <SummaryTile
            label="Covid-19"
            value={coversCovid(insurance) ? 'Coberto' : 'Não cobre'}
            muted={!coversCovid(insurance)}
          />
          <SummaryTile
            label="Cancelamento"
            value={cancellation && cancellation.status !== 'no' ? cancellation.display : 'Não informado'}
            muted={!cancellation || cancellation.status === 'no'}
          />
        </Box>

        {/* Avisos importantes (validade territorial, idade, duração) */}
        {details.notices.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.75,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.08),
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.3),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <InfoOutlined sx={{ color: 'warning.dark', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Importante</Typography>
            </Stack>
            {details.notices.map((notice) => (
              <Typography key={notice.label} sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                <strong>{notice.label}:</strong> {notice.text}
              </Typography>
            ))}
          </Box>
        )}

        {/* Coberturas por assunto */}
        {details.sections.map((section) => (
          <Box key={section.title} sx={{ mt: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.8rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'primary.main',
                mb: 0.5,
              }}
            >
              {section.title}
            </Typography>
            {section.items.map((item) => (
              <CoverageRow key={item.label} item={item} />
            ))}
          </Box>
        ))}

        {details.sections.length === 0 && (
          <Typography sx={{ mt: 3, color: 'text.secondary' }}>
            A seguradora não enviou a lista de coberturas deste plano.
          </Typography>
        )}

        <Typography sx={{ mt: 3, fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.5 }}>
          Valores máximos por pessoa, conforme as condições gerais da seguradora.
          {details.footnotes.map((text) => (
            <Box component="span" key={text} sx={{ display: 'block', mt: 1 }}>
              {text}
            </Box>
          ))}
        </Typography>
      </Box>

      {/* Rodapé fixo com preço e compra */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.3rem' }, lineHeight: 1.1 }}>
            {formatBRL(total)}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {passengers > 1 ? `total para ${passengers} pessoas` : 'total'}
            {perDay > 0 && ` · ${formatBRL(perDay)}/dia`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => selectInsurance(insurance)}
          endIcon={<East sx={{ transform: 'rotate(-45deg)' }} />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Selecionar
        </Button>
      </Box>
    </Dialog>
  );
};

export default CoverageDialog;
